import { useTranslation } from 'react-i18next';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import { useLocation, useNavigate } from 'react-router-dom';
import { ITicket } from '../../interfaces/support';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { useTicketStatusColors } from '../../hooks/useTicketStatusColors';
import { useDebounce } from 'use-debounce';
export default function TicketsListTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setfetchedData] = useState<ITicket[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const { pathname } = useLocation();

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewTicket = (id: number) => () => {
		if (pathname.startsWith('/admin')) {
			navigate(`/admin/support/view/${id}`);
		} else {
			navigate(`/support/view/${id}`);
		}
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const createNewTicket = () => {
		if (pathname.startsWith('/admin')) {
			navigate(`/admin/tickets/list`);
		} else {
			navigate(`/support/new-ticket`);
		}
	};
	const getServer = () => {
		setLoading(true);
		axios<IResponse<ITicket[]>>({
			url: '/tickets',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setfetchedData(res.data.data.map(ticket => ({ ...ticket, ticket_id: ticket.id })));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	const properties: (keyof ITicket)[] = ['title'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<ITicket>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'ticket_id',
			headerName: t('ticket_id'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'title',
			headerName: t('title'),
			width: 200,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 180,
			cellClassName: 'flex items-center',
			headerAlign: 'left',
			headerClassName: `${headerClassNames} ps-12`,
			align: 'left',
			renderCell: params => {
				const isPending = params.row.status === 'pending';
				const label = isPending ? t('awaiting_reply') : t(params.row.status);
				const bgColorClass = useTicketStatusColors(params.value);
				return <div className={`ms-10 flex h-[min-content] w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{label}</div>;
			},
		},
		{
			field: 'created_at',
			headerName: t('start_date'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton aria-label="eye" onClick={handleViewTicket(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	return (
		<div>
			<BaranDataGrid<ITicket> loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={createNewTicket} actionButtonLable={t('create_new_ticket')} searchableFields={properties} />;
		</div>
	);
}
